// MI-282 Rev2: the review signals a movement can trip. ALERT_KEYS is the
// canonical list (order = display priority) reused by the consolidated alert
// icon, the Settings toggles, and the per-user preferences store.
export const ALERT_KEYS = [
  'incomplete',
  'halfConfirmed',
  'needsSourceFile',
  'accountMismatch',
  'wrongCurrency',
  'exchangeMissing',
  'sourceRules',
];

// Active alerts for a movement, as short friendly labels for the consolidated
// icon (movements list + dashboard card). `t` is the movements-namespace
// translator. `prefs` is the user's alert-display map (key -> bool); a key is
// "on" unless explicitly false, so an empty map means all alerts show.
export function getMovementAlerts(movement, { t, prefs = {} } = {}) {
  if (!movement) return [];
  const on = (key) => prefs[key] !== false;
  const alerts = [];
  if (movement.is_incomplete && on('incomplete')) {
    alerts.push({ key: 'incomplete', label: t('list.tooltips.incomplete') });
  }
  if (movement.is_half_confirmed && on('halfConfirmed')) {
    alerts.push({ key: 'halfConfirmed', label: t('list.tooltips.halfConfirmed') });
  }
  if (movement.has_account_mismatch && on('accountMismatch')) {
    alerts.push({ key: 'accountMismatch', label: t('list.tooltips.accountMismatch') });
  }
  if (movement.is_wrong_currency && on('wrongCurrency')) {
    alerts.push({ key: 'wrongCurrency', label: t('list.tooltips.wrongCurrency') });
  }
  if (movement.is_exchange_missing && on('exchangeMissing')) {
    alerts.push({ key: 'exchangeMissing', label: t('list.tooltips.exchangeMissing') });
  }
  if (movement.needs_source_file && on('needsSourceFile')) {
    alerts.push({ key: 'needsSourceFile', label: t('list.tooltips.needsSourceFile') });
  }
  if (movement.needs_source_rule_review && on('sourceRules')) {
    alerts.push({ key: 'sourceRules', label: t('list.tooltips.sourceRules') });
  }
  return alerts;
}

// The backend review flag behind each preference that the user can fix. The
// alert filter accepts only MOVEMENT_REVIEW_FLAGS values and rejects the whole
// request on an unknown one, so this map moves in lockstep with that list and
// with ALERT_KEYS. `needsSourceFile` is left out on purpose: a movement that
// is only waiting for its bank row has nothing to fix.
export const PREF_TO_FLAG = {
  incomplete: 'is_incomplete',
  halfConfirmed: 'is_half_confirmed',
  accountMismatch: 'has_account_mismatch',
  wrongCurrency: 'is_wrong_currency',
  exchangeMissing: 'is_exchange_missing',
  sourceRules: 'needs_source_rule_review',
};

export const WAITING_FLAG = 'needs_source_file';

// The flags the user's preferences leave on (a key is on unless === false).
export function fixableFlags(prefs = {}) {
  return Object.keys(PREF_TO_FLAG)
    .filter((key) => prefs[key] !== false)
    .map((key) => PREF_TO_FLAG[key]);
}

const alertCondition = (operator, values) => ({ entity: 'Movement', column: 'alert', operator, values });
const NOT_IGNORED = { entity: 'Movement', column: 'is_ignored', operator: 'eq', values: [false] };

/**
 * The filters behind the review count: `{ fixable, waiting }`, each a filter
 * list or null when there is nothing to ask. An alert condition with no values
 * matches every movement (`in []`) or none (`not in []`), so an empty list is
 * never sent: no fixable flag means no fixable query, and the waiting query
 * then drops its `not in` condition. `is_ignored eq false` is always added,
 * because the per-flag conditions do not skip ignored movements.
 */
export function reviewFilters(prefs = {}) {
  const fixable = fixableFlags(prefs);
  const waitingOn = prefs.needsSourceFile !== false;
  return {
    fixable: fixable.length ? [alertCondition('in', fixable), NOT_IGNORED] : null,
    waiting: waitingOn
      ? [
        alertCondition('in', [WAITING_FLAG]),
        ...(fixable.length ? [alertCondition('not_in', fixable)] : []),
        NOT_IGNORED,
      ]
      : null,
  };
}
